import { skipToken } from "@reduxjs/toolkit/query";
import { Color } from "@repo/app-types/constants/colors";
import type { CriteriaFilter } from "@repo/app-types/entities/filters";
import {
  getOutcomeNoun,
  isRevenueOutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import {
  getOutcomeDefinitionFromPlaybook,
  type PlaybookCriterionId,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import {
  buildTeammateDisplayNames,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import type { CriteriaScoreDistribution } from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  findCriterionInSubtree,
  parseGradingValue,
  type GradingValue,
} from "@repo/app-types/helpers/gradingAssessmentHelpers";
import { getOutcomeImpactCopy } from "@repo/app-types/helpers/outcomeImpactCopy";
import {
  assertUnreachable,
  isNotNullNotUndefined,
} from "@repo/app-types/helpers/utilities";
import { Size } from "@repo/ui/helpers/helpers";
import { Pill } from "@repo/ui/Pills/pill";
import { PillSkeleton } from "@repo/ui/Pills/pillSkeleton";
import {
  SlideoverDialog,
  SlideoverDialogContentGray,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { useMemo, useState, type FC, type PropsWithChildren } from "react";
import {
  usePlaybookTeammateMostRecentGradingsInfiniteQuery,
  usePlaybookTeammatePlaybookCriteriaScoresQuery,
  useTeammateHighImpactCriteriaQuery,
} from "../../api/entities/performancePlaybook.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useBasicTeammateQuery } from "../../api/entities/teammate.js";
import { useAppSelector } from "../../hooks/hook.js";
import { useModalNavigation } from "../../hooks/useModalNavigation.js";
import { buildTeamPerformancePageQueryArgs } from "../filters/buildFilterQueryArgs.js";
import { useFilters } from "../filters/filters.js";
import { selectTeammate } from "../teammate/teammateSlice.js";
import { buildCriteriaOutcomeImpactTooltip } from "./criteriaOutcomeCorrelationImpactPill.js";
import { CriterionAverageScore } from "./criterionAverageScore.js";
import { CriterionGradingsTable } from "./criterionGradingsTable.js";
import { CriterionScoreDistributionBar } from "./criterionScoreDistributionBar.js";
import { CriterionTeamExamplesSkeleton } from "./criterionTeamExamples.js";
import { CriterionTeamExamplesContainer } from "./criterionTeamExamplesContainer.js";

//
// Props.
//

interface CriterionDialogProps {
  playbookId: PlaybookId;
  teammateId: TeammateId;
  criterionId: string;
}

//
// Component.
//

export const CriterionDialog: FC<CriterionDialogProps> = (props) => {
  const { playbookId, teammateId, criterionId } = props;

  const authenticatedTeammate = useAppSelector(selectTeammate);
  const { data: viewedTeammate } = useBasicTeammateQuery(
    authenticatedTeammate?.id !== teammateId ? teammateId : skipToken,
  );

  const now = useNow();

  // Use the filter values from the teammate detail page
  const [
    {
      dateRange,
      outcome,
      customFields,
      playbookConditions,
      signalId,
      signalMetadataFields,
      totalRecordingDuration,
      dateBasis,
    },
  ] = useFilters(
    [
      "dateRange",
      "outcome",
      "customFields",
      "playbookConditions",
      "signalId",
      "signalMetadataFields",
      "totalRecordingDuration",
      "dateBasis",
    ],
    { playbookId },
  );

  const baseArgs = buildTeamPerformancePageQueryArgs(now, {
    playbookId,
    dateRange,
    teammateGroupIds: null,
    outcome,
    customFields,
    playbookConditions,
    signalId,
    signalMetadataFields,
    totalRecordingDuration,
    dateBasis,
  });

  const {
    data: playbookCriteriaScoresData,
    isLoading: isLoadingPlaybookCriteriaScores,
  } = usePlaybookTeammatePlaybookCriteriaScoresQuery({
    playbookId,
    teammateId,
    scope: { ...baseArgs.scope, playbookId },
    filters: baseArgs.filters,
  });
  const playbookGradingsCount = playbookCriteriaScoresData?.gradingCount;
  const playbookCriteriaScores = playbookCriteriaScoresData?.criteriaScores;

  const { data: playbook, isLoading: isLoadingPlaybookById } =
    usePlaybookByIdQuery(playbookId);

  const outcomeDefinition = getOutcomeDefinitionFromPlaybook(playbook);

  const isLoadingPlaybook =
    isLoadingPlaybookCriteriaScores || isLoadingPlaybookById;

  const criterion = playbook
    ? findCriterionInSubtree(playbook.assessment_tree, criterionId)
    : undefined;
  const criterionValueType = criterion?.grading_value_type;
  const teammateScore = playbookCriteriaScores?.find(
    (item) => item.criteria_node_id === criterionId,
  );
  const criterionAverageScore = teammateScore?.average_score;
  const criterionScoreDistribution = teammateScore?.score_distribution;

  const outcomeNoun = outcomeDefinition
    ? getOutcomeNoun(outcomeDefinition)
    : undefined;
  const isRevenueOutcome = outcomeDefinition
    ? isRevenueOutcomeDefinition(outcomeDefinition)
    : undefined;

  const { data: highImpactCriteria } = useTeammateHighImpactCriteriaQuery({
    playbookId,
    teammateId,
    asOfDateIso: dateRange.end?.toISO() ?? now.endOf("day").toISO(),
  });

  const highImpactDetail = useMemo(
    () =>
      highImpactCriteria?.high_impact_criteria.find(
        (entry) =>
          entry.criteria_node_id === criterionId && entry.is_high_impact,
      ),
    [highImpactCriteria, criterionId],
  );

  const calibratedNetConversions = highImpactDetail?.net_conversions ?? 0;
  const averageRevenuePerOpp =
    highImpactCriteria?.average_revenue_per_opp ?? undefined;
  const impactCopy = getOutcomeImpactCopy({
    netConversions: calibratedNetConversions,
    averageRevenuePerOpp,
    outcomeNoun,
    outcomePluralNoun: outcomeDefinition?.plural_noun,
    isRevenueOutcome,
  });

  // Only show the impact card when criterion is in the high impact list
  const shouldShowImpactCard =
    highImpactDetail !== undefined &&
    highImpactCriteria !== undefined &&
    outcomeNoun !== undefined;

  // If all gradings are not applicable, default to showing N/A gradings. Otherwise, default to showing applicable gradings (= null).
  const defaultFilterValue = checkIsAllNotApplicable(criterionScoreDistribution)
    ? "N/A"
    : null;
  const [customFilterValue, setFilterValue] = useState<
    string | null | undefined
  >(undefined);

  // The effective filter is either the one selected by the user or the default.
  const filterValue =
    customFilterValue !== undefined ? customFilterValue : defaultFilterValue;

  const parsedFilterValue =
    criterionValueType && filterValue
      ? parseGradingValue(criterionValueType, filterValue)
      : undefined;

  // When listing gradings, append the criterion-level filter:
  //   - default (nothing selected)  → only applicable gradings
  //   - "N/A" selected               → only not-applicable gradings
  //   - specific score selected      → exact match
  const gradingsArgs = criterionValueType
    ? {
        playbookId,
        teammateId,
        scope: { ...baseArgs.scope, playbookId },
        filters: [
          ...baseArgs.filters,
          ...createCriterionFiltersFromGradingValue(
            criterionId,
            criterionValueType,
            parsedFilterValue,
          ),
        ],
      }
    : skipToken;

  // Paginate full account gradings to show the table
  const {
    data: gradingsData,
    isFetching: isFetchingGradings,
    isError: isErrorGradings,
    hasNextPage: canFetchMoreGradings,
    fetchNextPage: fetchMoreGradings,
  } = usePlaybookTeammateMostRecentGradingsInfiniteQuery(gradingsArgs);

  // Flatten the pages and extract the criterion score
  const gradings = useMemo(() => {
    if (!criterionValueType || !gradingsData) {
      return undefined;
    }

    return gradingsData.pages
      .flatMap((page) => page.recentGradings)
      .map((grading) => {
        if (!grading.assessment) {
          return undefined;
        }

        const criterionGrade = grading.assessment.criteria_grading[criterionId];
        const parsedCriterionValue = criterionGrade
          ? parseGradingValue(criterionValueType, criterionGrade.value)
          : undefined;

        return {
          ...grading,
          parsedCriterionValue,
        };
      })
      .filter(isNotNullNotUndefined);
  }, [criterionId, criterionValueType, gradingsData]);

  // Filter the account gradings based on the selected filter
  const filteredGradings = useMemo(() => {
    if (!gradings) {
      return undefined;
    }

    return gradings.filter((grading) => {
      // If no filter is selected, filter out accounts without a grade or with an N/A grade
      if (parsedFilterValue === undefined) {
        return isNotNullNotUndefined(grading.parsedCriterionValue?.value);
      }

      // Otherwise, keep only accounts that match the selected filter
      const gradingValue = grading.parsedCriterionValue?.value ?? null;
      return gradingValue === parsedFilterValue.value;
    });
  }, [parsedFilterValue, gradings]);

  const renderPerformanceSummaryContent = () => {
    if (isLoadingPlaybook) {
      return (
        <>
          <PillSkeleton size={Size.SMALL} />
          <span className="text-sm text-muted-foreground">over</span>
          <PillSkeleton size={Size.SMALL} />
          <span className="text-sm text-muted-foreground">
            customer interactions
          </span>
        </>
      );
    }

    const interactionsLabel = playbookGradingsCount?.toLocaleString() ?? "--";

    if (
      criterion &&
      criterionAverageScore !== undefined &&
      !Number.isNaN(criterionAverageScore)
    ) {
      return (
        <>
          <CriterionAverageScore
            valueType={criterion.grading_value_type}
            value={criterionAverageScore}
          />
          <span className="text-sm text-muted-foreground">over</span>
          <Pill
            color={Color.Blue}
            size={Size.SMALL}
            label={interactionsLabel}
          />
          <span className="text-sm text-muted-foreground">
            customer interactions
          </span>
        </>
      );
    }

    return (
      <>
        <span className="text-sm text-muted-foreground">-- over</span>
        <Pill color={Color.Blue} size={Size.SMALL} label={interactionsLabel} />
        <span className="text-sm text-muted-foreground">
          customer interactions
        </span>
      </>
    );
  };

  const ownerLabel = useMemo(() => {
    if (authenticatedTeammate?.id === teammateId) {
      return "Your";
    }

    const viewedName = viewedTeammate
      ? buildTeammateDisplayNames(viewedTeammate).primaryDisplayName
      : undefined;

    return viewedName ? `${viewedName}'s` : "Teammate's";
  }, [authenticatedTeammate?.id, teammateId, viewedTeammate]);

  const recentScoresHeading = `${ownerLabel} recent scores on this criterion`;

  const recordingSlideoverNavigation = useModalNavigation();
  return (
    <SlideoverDialog label={criterion?.name || "Criterion dialog"}>
      <SlideoverDialogTitleHeader isLoading={isLoadingPlaybook}>
        {criterion?.name}
      </SlideoverDialogTitleHeader>
      <SlideoverDialogContentGray>
        <div className="flex flex-col gap-5 px-4 py-4">
          {criterion ? (
            <CriterionTeamExamplesContainer
              playbookId={playbookId}
              teammateId={teammateId}
              criterion={criterion}
            />
          ) : (
            <CriterionTeamExamplesSkeleton />
          )}

          <div className="flex flex-col gap-3">
            <div
              className="truncate text-sm font-semibold text-foreground"
              title={recentScoresHeading}
            >
              {recentScoresHeading}
            </div>
            <div className="flex flex-wrap gap-3">
              <TopCard>
                <div className="flex items-center gap-1.5">
                  {renderPerformanceSummaryContent()}
                </div>
              </TopCard>
              {shouldShowImpactCard && (
                <TooltipTrigger>
                  <TooltipHoverTarget shape="rounded">
                    <TopCard>
                      <TopCardText>
                        {outcomeNoun.charAt(0).toUpperCase() +
                          outcomeNoun.slice(1)}{" "}
                        impact:
                      </TopCardText>
                      <Pill
                        color={Color.Emerald}
                        size={Size.SMALL}
                        label={impactCopy.dialogLabel}
                      />
                    </TopCard>
                  </TooltipHoverTarget>
                  <SimpleTooltip>
                    {buildCriteriaOutcomeImpactTooltip({
                      rank: highImpactDetail.rank,
                      totalCriteria: highImpactCriteria.total_criteria_count,
                      netConversions: Math.round(calibratedNetConversions),
                      averageRevenuePerOpp,
                      outcomeNoun,
                      outcomePluralNoun: outcomeDefinition?.plural_noun,
                      isRevenueOutcome,
                      isTeammateLevel: true,
                    })}
                  </SimpleTooltip>
                </TooltipTrigger>
              )}
            </div>
            <CriterionScoreDistributionBar
              distribution={criterionScoreDistribution}
              selectedValue={filterValue}
              onValueSelect={setFilterValue}
              isLoading={isLoadingPlaybook}
            />
            <CriterionGradingsTable
              criterion={criterion}
              gradings={filteredGradings ?? []}
              unfilteredEntityCount={gradings?.length ?? 0}
              isLoading={isLoadingPlaybook || isFetchingGradings}
              isError={isErrorGradings}
              onLoadMore={canFetchMoreGradings ? fetchMoreGradings : undefined}
              onRecordingAction={(grading) => {
                recordingSlideoverNavigation.push(
                  `customer-interactions/${encodeURIComponent(grading.id)}`,
                );
              }}
            />
          </div>
        </div>
      </SlideoverDialogContentGray>
    </SlideoverDialog>
  );
};

const TopCard: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  return (
    <div className="flex items-center gap-3 rounded-md bg-white px-3 py-2 shadow-sm ring-1 ring-black/5">
      {children}
    </div>
  );
};

const TopCardText: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  return <div className="text-md">{children}</div>;
};

/** Check if all gradings have a N/A for the criterion. */
function checkIsAllNotApplicable(
  criterionScoreDistribution: CriteriaScoreDistribution | undefined,
) {
  const notApplicableCount = criterionScoreDistribution?.counts["N/A"] ?? 0;
  const totalCount = criterionScoreDistribution
    ? Object.values(criterionScoreDistribution.counts).reduce(
        (a = 0, b = 0) => a + b,
        0,
      )
    : 0;

  const isAllNotApplicable = criterionScoreDistribution
    ? notApplicableCount > 0 && notApplicableCount === totalCount
    : false;

  return isAllNotApplicable;
}

function createCriterionFiltersFromGradingValue(
  criterionId: PlaybookCriterionId,
  valueType: "yes_no" | "1_5_score",
  value: GradingValue | undefined,
): CriteriaFilter[] {
  // Nothing selected → restrict to gradings where the criterion is applicable.
  if (value === undefined) {
    return [
      {
        type: "criteria",
        id: criterionId,
        score: { valueType, op: "is_applicable" },
      },
    ];
  }

  // "N/A" bucket selected → restrict to gradings where the criterion is not
  // applicable.
  if (value.value === null) {
    return [
      {
        type: "criteria",
        id: criterionId,
        score: { valueType, op: "is_not_applicable" },
      },
    ];
  }

  switch (value.type) {
    case "yes_no":
      return [
        {
          type: "criteria",
          id: criterionId,
          score: {
            valueType: value.type,
            value: value.value,
          },
        },
      ];

    case "1_5_score":
      return [
        {
          type: "criteria",
          id: criterionId,
          score: {
            op: "=",
            valueType: value.type,
            value: value.value,
          },
        },
      ];

    default:
      return assertUnreachable(value, () => []);
  }
}
